export const URLS = {
  HOME: "/",
  CONTACT_US: "/contact-us",
  ABOUT: "/about",
  PRIVACY: "/privacy",
  SERVICES: {
    ROOT: "/services",
    ACCOUNTING: {
      ROOT: "/services/accounting",
      ACCOUNTING_SYSTEM_SELECTION_AND_SETUP:
        "/services/accounting/accounting-system-selection-and-setup",
      COMPILATION_OF_FINANCIAL_STATEMENTS:
        "/services/accounting/compilation-of-financial-statements",
      FINANCIAL_POSITION_JOB_DESCRIPTIONS:
        "/services/accounting/financial-position-job-descriptions",
      POLICIES_AND_PROCEDURES: "/services/accounting/policies-and-procedures",
      PREPARATION_OF_FINANCIAL_STATEMENTS:
        "/services/accounting/preparation-of-financial-statements",
      SOFTWARE_SELECTION_AND_IMPLEMENTATION:
        "/services/accounting/software-selection-and-implementation",
      QUICKBOOKS_SERVICES: "/services/accounting/quickbooks-services",
    },
    ADVISORY: {
      ROOT: "/services/advisory",
      BUDGETING_AND_FORECASTS: "/services/advisory/budgeting-and-forecasts",
      CASH_FLOW_MANAGEMENT: "/services/advisory/cash-flow-management",
      DISCLOSURES_AND_BUDGET_SUPPORT:
        "/services/advisory/disclosures-and-budget-support",
      GAAP_IMPLEMENTATION: "/services/advisory/gaap-implementation",
      MEDICARE_COST_REPORTS: "/services/advisory/medicare-cost-reports",
      SUPPLEMENTAL_SCHEDULES: "/services/advisory/supplemental-schedules",
      SUCCESSION_PLANNING_AND_WEALTH_TRANSFER:
        "/services/advisory/succession-planning-and-wealth-transfer",
    },
    AUDIT: {
      ROOT: "/services/audit",
      AUDIT_OF_FINANCIAL_STATEMENTS:
        "/services/audit/audit-of-financial-statements",
      AUDITS_REVIEWS_AND_FINANCIAL_STATEMENTS:
        "/services/audit/audits-reviews-and-financial-statements",
      INTERNAL_CONTROL_DOCUMENTATION:
        "/services/audit/internal-control-documentation",
      INTERNAL_CONTROLS: "/services/audit/internal-controls",
      REVIEW_OF_FINANCIAL_STATEMENTS:
        "/services/audit/review-of-financial-statements",
      SPECIAL_PURPOSE_FINANCIAL_STATEMENTS:
        "/services/audit/special-purpose-financial-statements",
    },
    BUSINESS: {
      ROOT: "/services/business",
      PART_TIME_CONTROLLER_CFO: "/services/business/part-time-controller-cfo",
      DEBT_COVENANT_COMPLIANCE: "/services/business/debt-covenant-compliance",
      DEVELOPMENT_OF_BUDGETS: "/services/business/development-of-budgets",
      DEVELOPMENT_OF_FINANCIAL_POLICIES_AND_PROCEDURES:
        "/services/business/development-of-financial-policies-and-procedures",
      SUCCESSFUL_PLANNING: "/services/business/successful-planning",
    },
    INDIVIDUAL: {
      ROOT: "/services/individual",
      PERSONAL_FINANCIAL_STATEMENTS:
        "/services/individual/personal-financial-statements",
      INDIVIDUAL_ACCOUNTING: "/services/individual/individual-accounting",
    },
    TAX: {
      ROOT: "/services/tax",
      ESTATE_AND_TRUSTS: "/services/tax/estate-and-trusts",
      FEDERAL_TAX_RETURN_PREPARATION:
        "/services/tax/federal-tax-return-preparation",
      STATE_FRANCHISE_INCOME_AND_TAX_RETURN:
        "/services/tax/state-franchise-income-and-tax-return",
      TAX_COMPLIANCE: "/services/tax/tax-compliance",
      IRS_RESOLUTION: "/services/tax/irs-resolution",
      OIL_AND_GAS_ROYALTY_TAX: "/services/tax/oil-and-gas-royalty-tax",
      RANCH_AND_LANDOWNER_TAX: "/services/tax/ranch-and-landowner-tax",
      REAL_ESTATE_INVESTOR_TAX: "/services/tax/real-estate-investor-tax",
      BUSINESS_SALE_EXIT: "/services/tax/business-sale-exit",
      TAX_PLANNING: "/services/tax/tax-planning",
    },
  },
  INDUSTRIES: {
    ROOT: "/industries",
    MANUFACTURING: "/industries/manufacturing",
    CONSTRUCTION: "/industries/construction",
    REAL_ESTATE: "/industries/real-estate",
    TECHNOLOGY: "/industries/technology",
    TRANSPORTATION: "/industries/transportation",
    OIL_AND_GAS: "/industries/oil-and-gas",
    HEALTHCARE: "/industries/healthcare",
    RANCHING_AND_AGRIBUSINESS: "/industries/ranching-and-agribusiness",
    PROFESSIONAL: "/industries/professional",
    NONPROFITS: "/industries/nonprofits",
  },
  LOCATIONS: {
    // Only the /locations hub exists today. Per-area detail pages were never
    // built, so they are intentionally NOT listed here (keeps them out of the
    // sitemap and avoids advertising 404s to Google).
    ROOT: "/locations",
  },
} as const;

export type UrlTree = typeof URLS;

export type LeafPaths<T, Prefix extends string = ""> = T extends string
  ? Prefix | `${Prefix}/${string}`
  : {
      [K in keyof T]: LeafPaths<
        T[K],
        K extends string
          ? `${Prefix}${Prefix extends "" ? "" : "/"}${Lowercase<K>}`
          : never
      >;
    }[keyof T];

export type AllUrlPaths = LeafPaths<UrlTree>;

export type ExtractUrls<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? ExtractUrls<T[keyof T]>
    : never;

export type AllUrlString = ExtractUrls<UrlTree>;

export function toServiceSlug(key: string): string {
  return key.toLowerCase().replace(/_/g, "-");
}

export function generateServiceCategoryParams() {
  return Object.keys(URLS.SERVICES)
    .filter(key => key !== "ROOT")
    .map(key => ({
      category: key.toLowerCase(),
    }));
}

export function generateServiceDetailParams() {
  const result: { category: string; service: string }[] = [];

  for (const [categoryKey, value] of Object.entries(URLS.SERVICES)) {
    if (categoryKey === "ROOT") {
      continue;
    }

    const entries = Object.entries(value as Record<string, string>);

    for (const [serviceKey, _path] of entries) {
      if (serviceKey === "ROOT") {
        continue;
      }

      result.push({
        category: categoryKey.toLowerCase(),
        service: toServiceSlug(serviceKey),
      });
    }
  }

  return result;
}

export function flattenUrls<T>(
  obj: UrlTree = URLS,
  transformer: (
    value: [key: string, value: string],
    index: number,
    array: [string, string | Record<string, unknown>][],
  ) => T = () => null as T,
  excludedKeys: string[] = ["home", "root"],
  parentKey: string | null = null,
): T[] {
  return Object.entries(obj).flatMap(([key, value], index, array) => {
    if (excludedKeys.includes(key.toLowerCase())) {
      return [];
    }

    // If the key is ROOT, use the parentKey instead
    const displayKey = key === "ROOT" && parentKey ? parentKey : key;

    if (typeof value === "string") {
      return [transformer([displayKey, value], index, array)];
    }

    if (isUrlTree(value)) {
      return flattenUrls(value, transformer, excludedKeys, displayKey);
    }

    return [];
  });
}

function isUrlTree(value: unknown): value is UrlTree {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).some(
      v => typeof v === "string" || typeof v === "object",
    )
  );
}
